import {
  browserTimeZoneKey,
  TimezoneSelectionOption,
  UTC_ZONE,
} from '@/models/time.models';
import { browserTimeZone, getTimezoneOffset } from '@/utils/time';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '../Button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../Command';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';

type TimezoneSelectionProps = {
  timezone?: string;
  placeholder?: string;
  allowToggle?: boolean;
  modal?: boolean;
  extraTimezone?: TimezoneSelectionOption;
  size?: 'default' | 'sm' | 'lg';
  setTimezone?: (timezone?: string) => void;
};
export function TimezoneSelection({
  timezone,
  placeholder = 'Select Timezone',
  allowToggle = true,
  extraTimezone,
  modal = false,
  size = 'default',
  setTimezone,
}: TimezoneSelectionProps) {
  const [open, setOpen] = useState(false);
  const [timezones, setTimezones] = useState<TimezoneSelectionOption[]>([
    {
      label: `Browser (${browserTimeZone})`,
      key: browserTimeZoneKey,
      timezone: browserTimeZone,
      offset: getTimezoneOffset(browserTimeZone),
    },
    {
      label: UTC_ZONE,
      key: UTC_ZONE,
      timezone: UTC_ZONE,
      offset: 'UTC+00:00',
    },
    ...(Intl.supportedValuesOf('timeZone') ?? []).map((tz) => {
      return {
        label: tz,
        key: tz,
        timezone: tz,
        offset: getTimezoneOffset(tz),
      };
    }),
  ]);

  useEffect(() => {
    if (extraTimezone) {
      setTimezones([extraTimezone, ...timezones]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraTimezone]);

  const handleTimezoneChange = (tz: string) => {
    if (setTimezone) {
      if (allowToggle && tz === timezone) {
        setTimezone(undefined);
      } else {
        setTimezone(tz);
      }
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={modal}>
      <PopoverTrigger asChild>
        <Button
          size={size}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full max-w-full justify-between"
        >
          {timezone ? (
            <span className="truncate">
              {timezones.find((tz) => tz.key === timezone)?.label}
            </span>
          ) : (
            <span className="text-muted-foreground truncate">
              {placeholder}
            </span>
          )}
          <ChevronsUpDown className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[375px] p-1" align="end" side="bottom">
        <Command defaultValue={timezone}>
          <CommandInput placeholder={'Search'} className="h-9" />
          <CommandList>
            <CommandEmpty className="text-muted-foreground flex justify-center py-2">
              Not found.
            </CommandEmpty>
            <CommandGroup>
              {timezones.map((tz) => {
                return (
                  <CommandItem
                    key={tz.key}
                    value={tz.key}
                    onSelect={handleTimezoneChange}
                    className="flex justify-between"
                  >
                    {tz.label}
                    <div className="flex justify-between gap-1">
                      <span className="text-muted-foreground text-xs">
                        {tz.offset}
                      </span>
                      <div className="w-4">
                        {timezone === tz.key && <Check />}
                      </div>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
